// Svelte 5 rune-backed project state.
//
// `projectState` is the single source of truth: the current Pipeline plus
// ProjectSettings, plus the currently-selected step and the active input
// asset metadata. Mutations should go through helpers in `$lib/model/tree`
// and `$lib/model/sync`, then assign back to trigger reactivity.

import type {
  InputAsset,
  Pipeline,
  ProjectSettings,
  Step,
} from '$lib/model/types';
import { DEFAULT_PROJECT_SETTINGS } from '$lib/model/types';
import { createEmptyPipeline } from '$lib/model/tree';

export interface ProjectState {
  name: string;
  settings: ProjectSettings;
  pipeline: Pipeline;
  layer_order: string[];
  selected_step_id: string | null;
  input_asset: InputAsset | null;
  // Raw decoded pixel buffer of the source image, post-rotation. Held outside
  // the cache because it is the root input every other step depends on.
  input_image: HTMLImageElement | null;
}

function makeInitialState(): ProjectState {
  const pipeline = createEmptyPipeline('untitled');
  return {
    name: 'untitled',
    settings: { ...DEFAULT_PROJECT_SETTINGS },
    pipeline,
    layer_order: [],
    selected_step_id: pipeline.root_id,
    input_asset: null,
    input_image: null,
  };
}

// Single global rune-backed store.
export const projectStore = $state<ProjectState>(makeInitialState());

// Replace the entire project state (used by recipe import + reset).
export function replaceProject(next: ProjectState): void {
  projectStore.name = next.name;
  projectStore.settings = next.settings;
  projectStore.pipeline = next.pipeline;
  projectStore.layer_order = next.layer_order;
  projectStore.selected_step_id = next.selected_step_id;
  projectStore.input_asset = next.input_asset;
  projectStore.input_image = next.input_image;
}

export function resetProject(): void {
  replaceProject(makeInitialState());
}

export function selectStep(id: string | null): void {
  projectStore.selected_step_id = id;
}

export function selectedStep(): Step | null {
  const id = projectStore.selected_step_id;
  if (!id) return null;
  return projectStore.pipeline.steps[id] ?? null;
}

// Returns true when the project has been edited beyond the initial empty state
// (used for the beforeunload warning).
export function isDirty(): boolean {
  if (projectStore.input_asset !== null) return true;
  if (Object.keys(projectStore.pipeline.steps).length > 1) return true;
  return false;
}
